import {
    faArchive,
    faClock,
    faPaperPlane,
    faPause,
    faPlay,
    faSquarePen,
    type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import type { VxBadgeColor } from '@voxie/frontend-components';
import { AUDIENCE_TYPES_MAP as MESSAGE_AUDIENCE_TYPES_MAP } from '~/constants/message';
import type { FieldBinding } from '~/types/snippet.types';

export const AUDIENCE_TYPE_MARKETING = 'marketing';
export const AUDIENCE_TYPE_TRANSACTIONAL = 'transactional';
export const AUDIENCE_TYPE_ACQUISITION = 'acquisition';
export const AUDIENCE_TYPE_RE_ACQUISITION = 're_acquisition';

export const MAX_SEGMENTS_PER_CAMPAIGN = 10;
export const MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN = 10;
export const MAX_CONTACTS_PER_REQUEST = 100;
export const MAX_SELECTED_CONTACTS_NUMBER = 15;

export const CAMPAIGN_STATUSES: Readonly<
    Record<
        CampaignStatus,
        {
            label: string;
            value: CampaignStatus;
            badge: VxBadgeColor;
            background: string;
            hexColor: string;
            icon: IconDefinition;
        }
    >
> = Object.freeze({
    draft: {
        label: 'Draft',
        value: 'draft',
        badge: 'amber',
        background: 'bg-slate-50 border-slate-100',
        hexColor: '#fbbf24', // amber-400
        icon: faSquarePen,
    },
    scheduled: {
        label: 'Scheduled',
        value: 'scheduled',
        badge: 'orange',
        background: 'bg-orange-50 border-orange-100',
        hexColor: '#fb923c', // orange-400
        icon: faClock,
    },
    published: {
        label: 'Live',
        value: 'published',
        badge: 'teal',
        background: 'bg-teal-50 border-teal-100',
        hexColor: '#2dd4bf', // teal-400
        icon: faPlay,
    },
    completed: {
        label: 'Completed',
        value: 'completed',
        badge: 'sky',
        background: 'bg-slate-50 border-slate-100',
        hexColor: '#38bdf8', // sky-400
        icon: faPaperPlane,
    },
    paused: {
        label: 'Paused',
        value: 'paused',
        badge: 'danger',
        background: 'bg-slate-50 border-slate-100',
        hexColor: '#fb7185', // rose-400
        icon: faPause,
    },
    archived: {
        label: 'Archived',
        value: 'archived',
        badge: 'slate',
        background: 'bg-slate-50 border-slate-100',
        hexColor: '#94a3b8', // slate-400
        icon: faArchive,
    },
});

export const statuses = {
    DRAFT: 'draft',
    SCHEDULED: 'scheduled',
    PUBLISHED: 'published',
    PAUSED: 'paused',
    COMPLETED: 'completed',
    ARCHIVED: 'archived',
} as const;

export type CampaignStatus = (typeof statuses)[keyof typeof statuses];

export const AUDIENCE_TYPES_MAP = {
    [AUDIENCE_TYPE_MARKETING]: MESSAGE_AUDIENCE_TYPES_MAP[AUDIENCE_TYPE_MARKETING],
    [AUDIENCE_TYPE_TRANSACTIONAL]: MESSAGE_AUDIENCE_TYPES_MAP[AUDIENCE_TYPE_TRANSACTIONAL],
    [AUDIENCE_TYPE_ACQUISITION]: MESSAGE_AUDIENCE_TYPES_MAP[AUDIENCE_TYPE_ACQUISITION],
    [AUDIENCE_TYPE_RE_ACQUISITION]: MESSAGE_AUDIENCE_TYPES_MAP[AUDIENCE_TYPE_RE_ACQUISITION],
} as const;

export type AudienceType = keyof typeof AUDIENCE_TYPES_MAP;

export const audienceTypes = () => Object.values(AUDIENCE_TYPES_MAP);

export function audienceTypeLabel(audienceType: AudienceType): string {
    return AUDIENCE_TYPES_MAP[audienceType]?.label || 'Unknown';
}

export function audienceTypeDescription(audienceType: AudienceType): string {
    return AUDIENCE_TYPES_MAP[audienceType]?.description || 'Unknown';
}

export const isDynamicFieldTypeFirstClass = (type: string) => ['contact_name', 'email'].includes(type);

export interface CampaignMessagePayload {
    snippet_id?: number | null;
    field_bindings?: FieldBinding[];
    body?: string;
    media_url?: string;
    media_content_type?: string;
    status?: string;
}

const isNotEmpty = (value: string | null | undefined): value is string => {
    return value !== null && value !== undefined && value !== '';
};

export const buildCampaignMessage = (params: {
    snippetId: number | null | undefined;
    snippetIsLocked: boolean;
    fieldBindings: FieldBinding[];
    body: string;
    mediaUrl: string | null | undefined;
    mediaContentType: string | null | undefined;
    status?: string;
}): CampaignMessagePayload => {
    if (params.snippetId && params.snippetIsLocked) {
        return {
            snippet_id: params.snippetId,
            field_bindings: (params.fieldBindings || []).filter((b) => isNotEmpty(b.value)),
            status: params.status || undefined,
        };
    }

    return {
        body: params.body,
        media_url: params.mediaUrl || undefined,
        media_content_type: params.mediaContentType || undefined,
        status: params.status || undefined,
    };
};

export const extractCampaignMessageErrors = (error: unknown): string[] => {
    const axiosError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
    const errors = axiosError?.response?.data?.errors;

    if (!errors) {
        return [];
    }

    const indexLevelErrors = errors['campaign_messages.0'] ?? [];
    const bodyFieldErrors = errors['campaign_messages.0.body'] ?? [];

    return [...indexLevelErrors, ...bodyFieldErrors];
};
